import React, { useMemo, useState } from "react";
import axios from "axios";
import {
    Paper,
    Group,
    Text,
    Button,
    Table,
    Modal,
    Stack,
    TextInput,
    ActionIcon,
    Tooltip,
    Select,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconPlus, IconPencil, IconTrash } from "@tabler/icons-react";
import type {
    TransportDepartureDto,
    TransportDepartureStopDto,
    TransportRouteStopDto,
} from "../types";

const apiUrl = process.env.REACT_APP_API_URL;
const RED = "#ff5e5e";

type Props = {
    departures: TransportDepartureDto[];
    selectedDepartureId: number | null;
    setSelectedDepartureId: (id: number | null) => void;
    departureStops: TransportDepartureStopDto[];
    availableRouteStops: TransportRouteStopDto[];
    refreshDepartureStops: () => Promise<void>;
};

export default function TransportRouteDepartureStopsTab({
    departures,
    selectedDepartureId,
    setSelectedDepartureId,
    departureStops,
    availableRouteStops,
    refreshDepartureStops,
}: Props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<TransportDepartureStopDto | null>(null);

    const [transportRouteStopId, setTransportRouteStopId] = useState<string | null>(null);
    const [pickupTime, setPickupTime] = useState("");
    const [saving, setSaving] = useState(false);

    const departureOptions = departures
        .filter((d) => d.isActive)
        .map((d) => ({
            value: String(d.transportDepartureID),
            label: `${d.routeName} • ${d.departureDate} • ${d.startTime}`,
        }));

    const routeStopOptions = availableRouteStops.map((s) => ({
        value: String(s.transportRouteStopID),
        label: `${s.stopOrder}. ${s.locationName}`,
    }));

    const selectedRouteStop = useMemo(
        () =>
            availableRouteStops.find(
                (x) => String(x.transportRouteStopID) === transportRouteStopId
            ),
        [availableRouteStops, transportRouteStopId]
    );

    const openCreate = () => {
        setEditing(null);
        setTransportRouteStopId(routeStopOptions[0]?.value ?? null);
        setPickupTime("");
        setModalOpen(true);
    };

    const openEdit = (stop: TransportDepartureStopDto) => {
        setEditing(stop);
        setTransportRouteStopId(String(stop.transportRouteStopID));
        setPickupTime(stop.pickupTime);
        setModalOpen(true);
    };

    const save = async () => {
        if (!selectedDepartureId) return;
        if (!transportRouteStopId || !selectedRouteStop) {
            showNotification({
                title: "Mangler",
                message: "Vælg route stop",
                color: "red",
            });
            return;
        }

        if (!pickupTime.trim()) {
            showNotification({
                title: "Mangler",
                message: "Tid er påkrævet (HH:mm)",
                color: "red",
            });
            return;
        }

        try {
            setSaving(true);

            if (!editing) {
                await axios.post(`${apiUrl}/api/transport-route/admin/departure-stops`, {
                    transportDepartureID: selectedDepartureId,
                    transportRouteStopID: Number(transportRouteStopId),
                    transportRouteLocationID: selectedRouteStop.transportRouteLocationID,
                    stopOrder: selectedRouteStop.stopOrder,
                    pickupTime: pickupTime.trim(),
                });

                showNotification({
                    title: "Gemt",
                    message: "Stop-tid oprettet",
                });
            } else {
                await axios.put(
                    `${apiUrl}/api/transport-route/admin/departure-stops/${editing.transportDepartureStopID}`,
                    {
                        transportRouteStopID: Number(transportRouteStopId),
                        transportRouteLocationID: selectedRouteStop.transportRouteLocationID,
                        stopOrder: selectedRouteStop.stopOrder,
                        pickupTime: pickupTime.trim(),
                    }
                );

                showNotification({
                    title: "Gemt",
                    message: "Stop-tid opdateret",
                });
            }

            setModalOpen(false);
            await refreshDepartureStops();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke gemme stop-tid",
                color: "red",
            });
        } finally {
            setSaving(false);
        }
    };

    const deleteStop = async (id: number) => {
        try {
            await axios.delete(`${apiUrl}/api/transport-route/admin/departure-stops/${id}`);
            showNotification({
                title: "OK",
                message: "Stop-tid slettet",
            });
            await refreshDepartureStops();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke slette stop-tid",
                color: "red",
            });
        }
    };

    return (
        <>
            <Group justify="space-between" mb="sm">
                <Text fw={700}>Departure stops</Text>

                <Group>
                    <Select
                        placeholder="Vælg afgang"
                        data={departureOptions}
                        value={selectedDepartureId ? String(selectedDepartureId) : null}
                        onChange={(v) => setSelectedDepartureId(v ? Number(v) : null)}
                        searchable
                        w={360}
                    />

                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={openCreate}
                        disabled={!selectedDepartureId}
                        style={{ backgroundColor: RED }}
                    >
                        Opret stop-tid
                    </Button>
                </Group>
            </Group>

            {!selectedDepartureId ? (
                <Paper withBorder p="md" radius="md">
                    <Text c="dimmed">Vælg først en afgang.</Text>
                </Paper>
            ) : (
                <Paper withBorder p="md" radius="md">
                    <Table.ScrollContainer minWidth={900}>
                        <Table striped highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Stop</Table.Th>
                                    <Table.Th>Rækkefølge</Table.Th>
                                    <Table.Th>Tid</Table.Th>
                                    <Table.Th></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {departureStops.map((stop) => (
                                    <Table.Tr key={stop.transportDepartureStopID}>
                                        <Table.Td>{stop.locationName}</Table.Td>
                                        <Table.Td>{stop.stopOrder}</Table.Td>
                                        <Table.Td>{stop.pickupTime}</Table.Td>
                                        <Table.Td>
                                            <Group justify="flex-end" gap="xs">
                                                <Tooltip label="Rediger">
                                                    <ActionIcon
                                                        variant="light"
                                                        onClick={() => openEdit(stop)}
                                                    >
                                                        <IconPencil size={16} />
                                                    </ActionIcon>
                                                </Tooltip>

                                                <Tooltip label="Slet">
                                                    <ActionIcon
                                                        color="red"
                                                        variant="light"
                                                        onClick={() =>
                                                            deleteStop(stop.transportDepartureStopID)
                                                        }
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </Paper>
            )}

            <Modal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? "Rediger stop-tid" : "Opret stop-tid"}
                centered
            >
                <Stack>
                    <Select
                        label="Route stop"
                        data={routeStopOptions}
                        value={transportRouteStopId}
                        onChange={setTransportRouteStopId}
                        searchable
                    />

                    <TextInput
                        label="Tid (HH:mm)"
                        value={pickupTime}
                        onChange={(e) => setPickupTime(e.currentTarget.value)}
                    />

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setModalOpen(false)}>
                            Annullér
                        </Button>
                        <Button onClick={save} loading={saving}>
                            Gem
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}